import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Typography } from '../Typography';
import {
  GasLevel,
  levelSpec,
  GasLevelMeterRoot,
  HeaderRow,
  HeaderLeft,
  GasIconCircle,
  HeaderMeta,
  HeaderMetaRow,
  LevelSubtitle,
  LevelSeparator,
  EditInputWrapper,
  EditInput,
  EditInputCheckIcon,
  PencilButton,
  LevelBadge,
  ProgressTrack,
  ProgressFill,
  ProgressLabel,
  LimitZone,
  EmptyHint,
  SliderWrapper,
  StyledSlider,
  SliderLimitMark,
  SliderLabels,
  SliderLabel,
} from './styles';

export type { GasLevel } from './styles';

/**
 * Nível máximo selecionável (limite do tanque). O track continua exibindo a
 * escala completa 0–100%, mas o seletor não ultrapassa este valor.
 */
export const MAX_LEVEL = 85;

/**
 * GasLevelMeter — medidor de nível de gás com slider e edição manual.
 *
 * Thresholds de cor:
 *  0–30%  → vermelho (crítico)
 *  30–80% → amarelo  (atenção)
 *  80–100%→ verde    (adequado)
 *
 * Sempre controlado: o consumidor mantém `value` e `onChange`.
 * Modo edição inline (lápis): input numérico 0-100, confirma com Enter ou ✓.
 *
 * @example
 * const [gas, setGas] = useState(45);
 * <GasLevelMeter value={gas} onChange={setGas} />
 */
export type GasLevelMeterProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> & {
  /** Nível atual em % (0–100, inteiro). Controlado. */
  value: number;
  /** Callback disparado ao mover o slider ou confirmar edição manual. */
  onChange: (value: number) => void;
  /** Label da localização. @default 'Gás' */
  label?: string;
  /** Abre o modo edição ao montar (útil para stories/testes). @default false */
  editingInitial?: boolean;
  /** Destaca a borda em vermelho (ex.: validação ao avançar). @default false */
  error?: boolean;
};

export const getLevel = (v: number): GasLevel => {
  if (v <= 30) return 'low';
  if (v <= 80) return 'mid';
  return 'high';
};

const getSliderBackground = (value: number, level: GasLevel): string => {
  const { fill } = levelSpec[level];
  return `linear-gradient(to right, ${fill} 0%, ${fill} ${value}%, #F5F5F5 ${value}%, #F5F5F5 100%)`;
};

// ── SVG icons ──────────────────────────────────────────────────────────────

const GasIcon = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 2h10v2H7V2zm0 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm0 2v14h10V7H7zm2 2h6v2H9V9zm0 4h6v2H9v-2z" />
  </svg>
);

const PencilIcon = (): React.ReactElement => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L5.333 13.333 2 14l.667-3.333L11.333 2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = (): React.ReactElement => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2 6l3 3 5-5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Component ──────────────────────────────────────────────────────────────

let _uid = 0;

export const GasLevelMeter = ({
  value,
  onChange,
  label = 'Gás',
  editingInitial = false,
  error = false,
  ...rest
}: GasLevelMeterProps): React.ReactElement => {
  const [editing, setEditing] = useState(editingInitial);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const uid = useRef(`gas-${++_uid}`).current;
  const blurTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const level = getLevel(value);
  const isEmpty = value === 0;
  const isDarkText = value < 20;

  // Sync draft com valor externo quando não está editando
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  // Foco automático ao entrar em modo edição
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitEdit = useCallback((): void => {
    const parsed = parseInt(draft, 10);
    const clamped = isNaN(parsed)
      ? value
      : Math.min(MAX_LEVEL, Math.max(0, parsed));
    setDraft(String(clamped));
    onChange(clamped);
    setEditing(false);
  }, [draft, value, onChange]);

  const cancelEdit = useCallback((): void => {
    setDraft(String(value));
    setEditing(false);
  }, [value]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(Math.min(MAX_LEVEL, parseInt(e.target.value, 10)));
  };

  const handleEditKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    }
    if (e.key === 'Escape') cancelEdit();
  };

  // Usa timeout para não conflitar com clique no botão ✓
  const handleEditBlur = (): void => {
    blurTimerRef.current = setTimeout(commitEdit, 150);
  };

  const handleConfirmClick = (): void => {
    clearTimeout(blurTimerRef.current);
    commitEdit();
  };

  const handleEditInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    // Bloqueia chars não numéricos
    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
    handleEditKeyDown(e);
  };

  return (
    <GasLevelMeterRoot
      role="group"
      aria-label={`Medidor de nível de gás — ${label}`}
      $error={error}
      {...rest}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <HeaderRow>
        <HeaderLeft>
          <GasIconCircle $level={level}>
            <GasIcon />
          </GasIconCircle>

          <HeaderMeta>
            <Typography as="span" size="md" weight="bold" color="#3D3D3D">
              {label}
            </Typography>

            <HeaderMetaRow>
              {editing ? (
                <>
                  <label
                    htmlFor={`${uid}-input`}
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      overflow: 'hidden',
                    }}
                  >
                    Editar nível de gás
                  </label>
                  <EditInputWrapper>
                    <EditInput
                      ref={inputRef}
                      id={`${uid}-input`}
                      type="number"
                      min={0}
                      max={MAX_LEVEL}
                      step={1}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={handleEditInputKeyDown}
                      onBlur={handleEditBlur}
                      aria-label="Nível de gás em porcentagem (0 a 85)"
                    />
                    <EditInputCheckIcon>
                      <CheckIcon />
                    </EditInputCheckIcon>
                  </EditInputWrapper>
                </>
              ) : (
                <>
                  <LevelSubtitle>{`Nível atual: ${value}%`}</LevelSubtitle>
                  <LevelSeparator />
                  <PencilButton
                    type="button"
                    aria-label="Editar nível de gás"
                    onClick={() => setEditing(true)}
                  >
                    <PencilIcon />
                  </PencilButton>
                </>
              )}
            </HeaderMetaRow>
          </HeaderMeta>
        </HeaderLeft>

        <LevelBadge $level={level} aria-hidden="true">
          {`${value}%`}
        </LevelBadge>
      </HeaderRow>

      {/* ── Barra de progresso ─────────────────────────────────────── */}
      <ProgressTrack aria-hidden="true">
        {isEmpty ? null : <ProgressFill $value={value} $level={level} />}
        <LimitZone $from={MAX_LEVEL} />
        {isEmpty ? (
          <EmptyHint>Arraste o controle abaixo</EmptyHint>
        ) : (
          <ProgressLabel $level={level} $darkText={isDarkText}>
            {`${value}% de gás`}
          </ProgressLabel>
        )}
      </ProgressTrack>

      {/* ── Slider ─────────────────────────────────────────────────── */}
      <SliderWrapper>
        <StyledSlider
          type="range"
          id={`${uid}-slider`}
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={handleSliderChange}
          $background={getSliderBackground(value, level)}
          aria-label={`Nível de gás: ${value}%`}
          aria-valuemin={0}
          aria-valuemax={MAX_LEVEL}
          aria-valuenow={value}
          aria-valuetext={`${value} por cento de gás`}
        />
        <SliderLimitMark $at={MAX_LEVEL} aria-hidden="true" />
      </SliderWrapper>

      {/* ── Labels ─────────────────────────────────────────────────── */}
      <SliderLabels aria-hidden="true">
        <SliderLabel $align="start">Vazio (0%)</SliderLabel>
        <SliderLabel $align="end">Limite máximo do tanque (85%)</SliderLabel>
      </SliderLabels>
    </GasLevelMeterRoot>
  );
};

export default GasLevelMeter;
